import { marked } from "marked";
import DOMPurify from "dompurify";
import type { DocEntry, MenuGroup, OrgId } from "../types";
import { log } from "./log";
import { highlightCode } from "../devhub/lib/highlight";
import { INLINE_IMAGE_CLASS, isInlineImageUrl } from "../../../../packages/shared/src/devhub-inline-image.js";

// Org-relative location of each org's System Docs home + documentation map.
// The on-disk layout mirrors the menu, so the Overview lives in the "Start here"
// group folder; its map links are resolved relative to this path.
export const DOC_OVERVIEW_PATH = "start-here/overview.md";

// Rendered-HTML cache: parse+sanitize is pure on (md, breaks), and chat bubbles
// / doc panes re-render far more often than their content changes. Bounded FIFO
// (evict the oldest entry) so a long session can't grow it without limit.
const MD_CACHE_MAX = 200;
const mdCache = new Map<string, string>();

// Render markdown via marked + DOMPurify; fall back to escaped text when the
// sanitizer can't run. `breaks` maps single newlines to <br> — on for
// chat/summary, off for docs.
export function renderMarkdown(md: string, breaks = false): string {
  const key = (breaks ? "b:" : "p:") + md;
  const hit = mdCache.get(key);
  if (hit !== undefined) return hit;

  // Fail CLOSED: marked v12 does not sanitize, so we must never emit its raw
  // HTML unsanitized (a doc's `<img onerror>` would run on innerHTML). If
  // DOMPurify can't run in this environment or sanitize throws, show escaped
  // text instead.
  let html: string;
  try {
    html = DOMPurify.isSupported
      // `async: false` makes parse synchronous; marked's type keeps the union.
      ? DOMPurify.sanitize(marked.parse(md, { gfm: true, breaks, async: false }) as string)
      : `<pre>${esc(md)}</pre>`;
  } catch {
    html = `<pre>${esc(md)}</pre>`;
  }

  if (mdCache.size >= MD_CACHE_MAX) {
    const oldest = mdCache.keys().next().value;
    if (oldest !== undefined) mdCache.delete(oldest);
  }
  mdCache.set(key, html);
  return html;
}

// Guard content-derived URLs before they reach an `href`. React does not block
// `javascript:` URLs (only a dev warning), so a malicious page.yml `public_url`
// could run script on click. Return the URL only when it's http(s), else undefined.
export function safeHttpUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  return /^https?:\/\//i.test(url.trim()) ? url : undefined;
}

export function esc(str: unknown): string {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

// Compact relative timestamp ("just now", "5m ago", "3h ago", "2d ago", "4mo ago").
// Empty string for missing/unparseable input. Shared across the dashboard feeds.
export function ago(iso?: string | null): string {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return "";
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d < 30 ? `${d}d ago` : `${Math.floor(d / 30)}mo ago`;
}

export function orgLabel(org: string): string {
  if (org === "aid") return "AID";
  return org ? org.charAt(0).toUpperCase() + org.slice(1) : "";
}

// GitHub-style heading slugs so table-of-contents "#anchor" links resolve.
function slug(text: string): string {
  return (text || "").toLowerCase().trim().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-");
}

/**
 * A heading's NAME, which is not the same as everything inside it.
 *
 * An inline status pill (`<Pill type="beta"/>`, see devhub/lib/prose.ts) is a chip
 * ABOUT the section, not part of what it is called, so it reaches neither the
 * anchor id nor the "On this page" rail. The id is the load-bearing half:
 * `## Endpoints` and `## Endpoints <Pill type="beta"/>` would otherwise slug to
 * different anchors, so adding a pill to an existing heading would silently break
 * every inbound deep link to it. The deployed site strips them in exactly the same
 * place (devhub repo `content/render.ts`'s `headingLabel`) — the preview and the
 * published page must not disagree about a page's anchors.
 *
 * Early-returns when the heading holds no pill, so this is a no-op everywhere else.
 */
export function headingLabel(h: Element): string {
  if (!h.querySelector(".dh-pill")) return h.textContent || "";
  const clone = h.cloneNode(true) as Element;
  clone.querySelectorAll(".dh-pill").forEach((p) => p.remove());
  return (clone.textContent || "").replace(/\s+/g, " ").trim();
}

/**
 * Give every image whose URL carries the `#inline` marker the inline class
 * (ENG-57576).
 *
 * A DOM pass rather than a markdown transform, for the same reason
 * assignHeadingIds is one: the marker is on the `src` the renderer already
 * emitted, and rewriting the markdown to carry a class would mean emitting raw
 * HTML that DOMPurify then has to be trusted to keep.
 *
 * The deployed site does exactly this, in exactly this place (devhub repo
 * `content/render.ts`) — the preview and the published page must not disagree
 * about which images are icons. The class name lives in shared and both sides
 * import it.
 *
 * Early-returns on a page with no inline images, so it is a provable no-op on
 * the corpus as it stands today.
 */
export function markInlineImages(root: HTMLElement): void {
  root.querySelectorAll("img").forEach((img) => {
    // getAttribute, not `.src`: the DOM resolves `.src` to an absolute URL, and
    // a root-relative `/assets/x.png#inline` would still carry its fragment —
    // but the attribute is what was authored, and reading it keeps this
    // answering the same question the markdown asked.
    if (isInlineImageUrl(img.getAttribute("src") ?? "")) img.classList.add(INLINE_IMAGE_CLASS);
  });
}

export function assignHeadingIds(root: HTMLElement): void {
  const seen: Record<string, number> = {};
  root.querySelectorAll("h1,h2,h3,h4,h5,h6").forEach((h) => {
    let s = slug(headingLabel(h));
    if (!s) return;
    if (seen[s] == null) seen[s] = 0;
    else { seen[s]++; s = `${s}-${seen[s]}`; }
    h.id = s;
  });
}

export function scrollToAnchor(root: HTMLElement, id: string): void {
  if (!id) return;
  let el: Element | null = null;
  try {
    el = root.querySelector("#" + (window.CSS && CSS.escape ? CSS.escape(id) : id));
  } catch { /* ignore */ }
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

// Resolve a relative doc link ("../ARCHITECTURE.md", "services/X.md", "./Y.md")
// against the current doc's folder, into a docs-root-relative path.
export function resolveDocPath(fromPath: string, href: string): string {
  const dir = fromPath.includes("/") ? fromPath.slice(0, fromPath.lastIndexOf("/")) : "";
  const stack = dir ? dir.split("/") : [];
  for (const seg of href.split("/")) {
    if (seg === "" || seg === ".") continue;
    if (seg === "..") stack.pop();
    else stack.push(seg);
  }
  return stack.join("/");
}

// Keep navigation inside the viewer:
//  • external http(s)/mailto → open in a new tab
//  • "#anchor"               → smooth-scroll within the current doc
//  • "*.md" (+ optional #a) and DevHub folder-per-page links ("section/slug/")
//    → open that doc via `openDoc` (folder links open the compare view)
//  • other relative links    → swallowed (don't blow away the SPA)
// Callers without an in-viewer navigator pass openDoc: null — every relative
// link is then neutralized and handed to `onRelative`, so surfaces whose content
// references repo-relative paths that aren't routable (the common files, a
// repo's live .agents/ tree) can tag them instead of duplicating this walker.
export function wireDocLinks(
  root: HTMLElement,
  fromPath: string,
  openDoc: ((path: string, anchor?: string) => void) | null,
  onRelative?: (a: HTMLAnchorElement) => void,
): void {
  root.querySelectorAll<HTMLAnchorElement>("a[href]").forEach((a) => {
    const href = a.getAttribute("href") || "";
    if (/^(https?:|mailto:)/i.test(href)) {
      a.setAttribute("target", "_blank");
      a.setAttribute("rel", "noopener noreferrer");
      return;
    }
    if (href.startsWith("#")) {
      a.addEventListener("click", (e) => { e.preventDefault(); scrollToAnchor(root, href.slice(1)); });
      return;
    }
    const hi = href.indexOf("#");
    const pathPart = hi >= 0 ? href.slice(0, hi) : href;
    const hash = hi >= 0 ? href.slice(hi + 1) : "";
    if (!pathPart) return;
    if (openDoc && (pathPart.toLowerCase().endsWith(".md") || pathPart.endsWith("/"))) {
      const target = resolveDocPath(fromPath, pathPart);
      a.addEventListener("click", (e) => { e.preventDefault(); openDoc(target, hash); });
    } else {
      a.addEventListener("click", (e) => e.preventDefault());
      onRelative?.(a);
    }
  });
}

// Mermaid is the heaviest dependency in the app, so it is code-split and loaded
// only when a page actually contains a ```mermaid block; the import promise is
// shared so it initializes once. A failed load (offline) is retryable.
let mermaidLoad: Promise<typeof import("mermaid").default | null> | null = null;
function loadMermaid(): Promise<typeof import("mermaid").default | null> {
  if (!mermaidLoad) {
    mermaidLoad = import("mermaid")
      .then(({ default: mermaid }) => {
        // Diagrams are authored light (each carries its own %%{init}%% + classDef
        // palette); keep mermaid's default light theme and render on a light card.
        // htmlLabels:false renders labels as native SVG <text> so they aren't
        // washed out by this page's `.md` styles.
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: "strict",
          htmlLabels: false,
          flowchart: { htmlLabels: false },
          class: { htmlLabels: false },
        });
        return mermaid;
      })
      .catch((e: unknown) => {
        log.warn("Mermaid failed to load:", e);
        mermaidLoad = null; // allow a retry on the next diagram render
        return null;
      });
  }
  return mermaidLoad;
}

// Turn ```mermaid code blocks into rendered SVG. Each block is replaced in
// place; on failure (or if mermaid didn't load) the source block is left as-is.
export async function renderDiagrams(root: HTMLElement): Promise<void> {
  const blocks = root.querySelectorAll("pre > code.language-mermaid");
  if (!blocks.length) return;
  const mermaid = await loadMermaid();
  if (!mermaid) return;
  let i = 0;
  for (const code of Array.from(blocks)) {
    const pre = code.closest("pre");
    if (!pre) continue;
    const id = `mmd-${Date.now()}-${i++}`;
    try {
      await mermaid.parse(code.textContent || "");
      const { svg } = await mermaid.render(id, code.textContent || "");
      // Sanitize the rendered SVG before injecting it — the diagram source is
      // user-authored markdown. Skip the diagram entirely if DOMPurify can't run.
      if (!DOMPurify.isSupported) continue;
      const host = document.createElement("div");
      host.className = "mermaid-diagram";
      host.innerHTML = DOMPurify.sanitize(svg, { USE_PROFILES: { svg: true, svgFilters: true, html: true } });
      pre.replaceWith(host);
      attachDiagramControls(host);
    } catch (e) {
      log.warn("Mermaid render failed:", e);
    } finally {
      document
        .querySelectorAll(`body > [id="${id}"], body > [id="d${id}"]`)
        .forEach((n) => n.remove());
    }
  }
}

// Make a rendered diagram zoomable and pannable: wheel / buttons to zoom,
// drag to move, double-click (or the reset button) to recenter. The SVG is
// moved into a transformed canvas inside an overflow-hidden viewport; a small
// control cluster fades in on hover. No-op if the host holds no <svg>.
function attachDiagramControls(host: HTMLElement): void {
  const svg = host.querySelector("svg");
  if (!svg) return;

  host.classList.add("mermaid-viewport");
  const canvas = document.createElement("div");
  canvas.className = "mermaid-canvas";
  canvas.appendChild(svg); // re-parent the SVG under the transform target
  host.appendChild(canvas);

  const MIN = 0.3, MAX = 8;
  let scale = 1, tx = 0, ty = 0;
  const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));
  const apply = () => { canvas.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`; };

  // Zoom toward a viewport point (px,py) so the content under it stays put.
  const zoomAt = (factor: number, clientX: number, clientY: number) => {
    const rect = host.getBoundingClientRect();
    const px = clientX - rect.left, py = clientY - rect.top;
    const next = clamp(scale * factor, MIN, MAX);
    const k = next / scale;
    tx = px - k * (px - tx);
    ty = py - k * (py - ty);
    scale = next;
    apply();
  };
  const zoomCenter = (factor: number) => {
    const r = host.getBoundingClientRect();
    zoomAt(factor, r.left + r.width / 2, r.top + r.height / 2);
  };
  const reset = () => { scale = 1; tx = 0; ty = 0; apply(); };

  host.addEventListener("wheel", (e) => {
    e.preventDefault();
    zoomAt(e.deltaY < 0 ? 1.1 : 1 / 1.1, e.clientX, e.clientY);
  }, { passive: false });

  let dragging = false, lastX = 0, lastY = 0;
  host.addEventListener("pointerdown", (e) => {
    if ((e.target as HTMLElement).closest(".mermaid-controls")) return; // let buttons click
    dragging = true; lastX = e.clientX; lastY = e.clientY;
    host.classList.add("grabbing");
    try { host.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  });
  host.addEventListener("pointermove", (e) => {
    if (!dragging) return;
    tx += e.clientX - lastX; ty += e.clientY - lastY;
    lastX = e.clientX; lastY = e.clientY;
    apply();
  });
  const endDrag = (e: PointerEvent) => {
    dragging = false; host.classList.remove("grabbing");
    try { host.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
  };
  host.addEventListener("pointerup", endDrag);
  host.addEventListener("pointercancel", endDrag);
  host.addEventListener("dblclick", reset);

  const controls = document.createElement("div");
  controls.className = "mermaid-controls";
  const mkBtn = (label: string, title: string, onClick: () => void) => {
    const b = document.createElement("button");
    b.type = "button"; b.textContent = label; b.title = title; b.setAttribute("aria-label", title);
    b.addEventListener("click", (e) => { e.stopPropagation(); onClick(); });
    controls.appendChild(b);
  };
  mkBtn("+", "Zoom in", () => zoomCenter(1.2));
  mkBtn("−", "Zoom out", () => zoomCenter(1 / 1.2));
  mkBtn("↻", "Reset view", reset);
  host.appendChild(controls);

  apply();
}

// ── Fenced code blocks: language label + copy button ──────────────────────────
// A small post-render pass (a sibling to renderDiagrams) that turns a bare
// `<pre><code class="language-xxx">` into a labelled, copyable card — the same
// affordance the API sample panel offers, but themed for the reader's light/dark
// prose surface. No syntax highlighting and no new dependency: label + copy only.

// Nicely-cased display names for common fence languages; unknown tokens fall back
// to the raw token (kept lower-case, shown as-authored).
const CODE_LANG_LABELS: Record<string, string> = {
  js: "JavaScript", javascript: "JavaScript", mjs: "JavaScript", cjs: "JavaScript",
  ts: "TypeScript", typescript: "TypeScript", jsx: "JSX", tsx: "TSX",
  json: "JSON", jsonc: "JSON", json5: "JSON",
  yaml: "YAML", yml: "YAML", toml: "TOML", ini: "INI",
  html: "HTML", xml: "XML", css: "CSS", scss: "SCSS", sass: "Sass", less: "Less",
  sh: "Shell", bash: "Shell", shell: "Shell", zsh: "Shell", console: "Shell",
  py: "Python", python: "Python", rb: "Ruby", ruby: "Ruby",
  go: "Go", golang: "Go", rust: "Rust", rs: "Rust",
  java: "Java", kotlin: "Kotlin", kt: "Kotlin", swift: "Swift",
  objc: "Objective-C", "objective-c": "Objective-C",
  c: "C", cpp: "C++", "c++": "C++", cs: "C#", csharp: "C#",
  php: "PHP", sql: "SQL", graphql: "GraphQL", gql: "GraphQL",
  md: "Markdown", markdown: "Markdown", mdx: "MDX",
  dockerfile: "Dockerfile", docker: "Dockerfile",
  http: "HTTP", curl: "cURL", diff: "Diff", proto: "Protobuf",
  text: "Text", txt: "Text", plaintext: "Text",
};

// Pull the raw language token from a `<code>`'s class list ("language-ts hljs" →
// "ts"), lower-cased. Returns null when there is no language token or when it's a
// mermaid diagram (renderDiagrams owns those) — the caller then skips the block,
// leaving a plain fence untouched.
export function codeBlockLangToken(className: string): string | null {
  const m = /(?:^|\s)language-([\w#+.-]+)/i.exec(className || "");
  if (!m) return null;
  const token = m[1].toLowerCase();
  if (!token || token === "mermaid") return null;
  return token;
}

// Map a `<code>`'s language token to a nicely-cased display label (unknown tokens
// fall back to the raw token). Returns null under the same conditions as
// codeBlockLangToken (no language / mermaid).
export function codeBlockLang(className: string): string | null {
  const token = codeBlockLangToken(className);
  if (!token) return null;
  return CODE_LANG_LABELS[token] ?? token;
}

const COPY_LABEL = "Copy";
const COPIED_LABEL = "✓ Copied";

// Decorate each language-tagged fenced code block with a header carrying the
// language label + a Copy button, and syntax-highlight its body via the local
// tokenizer (highlightCode) — no highlighting dependency. Idempotent (skips
// blocks already wrapped in `.dh-code`) and a no-op for mermaid / no-language
// fences. Runs after renderDiagrams so mermaid <pre>s have already been replaced
// by their SVG.
export function decorateCodeBlocks(root: HTMLElement): void {
  const doc = root.ownerDocument;
  if (!doc) return;
  root.querySelectorAll("pre").forEach((pre) => {
    // Idempotent: a decorated block's <pre> already sits inside a `.dh-code`.
    if (pre.parentElement?.classList.contains("dh-code")) return;
    const code = pre.querySelector("code");
    if (!code) return;
    const token = codeBlockLangToken(code.className);
    if (!token) return; // no language / mermaid → leave the plain fence as-is
    const label = CODE_LANG_LABELS[token] ?? token;

    // Capture the raw source BEFORE highlighting so Copy always yields the exact
    // code text, never the tokenized markup.
    const raw = code.textContent ?? "";

    const wrap = doc.createElement("div");
    wrap.className = "dh-code";
    const head = doc.createElement("div");
    head.className = "dh-code-head";
    const lang = doc.createElement("span");
    lang.className = "dh-code-lang";
    lang.textContent = label;
    const btn = doc.createElement("button");
    btn.type = "button";
    btn.className = "dh-code-copy";
    btn.textContent = COPY_LABEL;
    btn.setAttribute("title", "Copy to clipboard");
    btn.addEventListener("click", () => {
      void (async () => {
        try {
          await navigator.clipboard.writeText(raw);
          btn.textContent = COPIED_LABEL;
          btn.classList.add("is-copied");
          setTimeout(() => {
            btn.textContent = COPY_LABEL;
            btn.classList.remove("is-copied");
          }, 1500);
        } catch { /* clipboard unavailable */ }
      })();
    });
    head.appendChild(lang);
    head.appendChild(btn);
    // Slot the <pre> into a card: wrapper takes its place, then holds head + pre.
    pre.replaceWith(wrap);
    wrap.appendChild(head);
    wrap.appendChild(pre);

    // Syntax-highlight the body: highlightCode escapes the HTML first, so the
    // injected markup is just our own `.tok-*` spans (XSS-safe). textContent
    // round-trips to `raw`, but Copy uses the captured `raw` regardless.
    code.innerHTML = highlightCode(raw, token);
  });
}

// Parse the "## Documentation map" section of OVERVIEW.md into menu groups.
// Group headers are bold lines (**Start here**); items are markdown list links.
// Pulls every link on a list line so compact "·"-separated rows all land in the
// menu. Returns null if no usable map is found.
export function parseDocMenu(md: string, valid: Set<string> | null): MenuGroup[] | null {
  if (!md) return null;
  const m = md.match(/^##\s+Documentation map\s*$/m);
  if (!m) return null;
  let section = md.slice(m.index! + m[0].length);
  const nextH2 = section.search(/^##\s+/m);
  if (nextH2 !== -1) section = section.slice(0, nextH2);

  const groups: MenuGroup[] = [];
  let cur: MenuGroup | null = null;
  for (const raw of section.split("\n")) {
    const line = raw.trim();
    if (/^[-*]\s/.test(line)) {
      const links = [...line.matchAll(/\[([^\]]+)\]\(([^)]+)\)/g)];
      if (!links.length) continue;
      if (!cur) { cur = { label: "Docs", items: [] }; groups.push(cur); }
      for (const lk of links) {
        const raw = lk[2].split("#")[0];
        if (!raw.toLowerCase().endsWith(".md")) continue;
        // Map links are written relative to the Overview's own folder (the on-disk
        // layout mirrors the menu); resolve them back to org-relative paths.
        const p = resolveDocPath(DOC_OVERVIEW_PATH, raw);
        if (valid && valid.size && !valid.has(p)) continue;
        cur.items.push({ title: friendlyName(lk[1].trim()), path: p });
      }
      continue;
    }
    const head = line.match(/^\*\*(.+?)\*\*/);
    if (head) { cur = { label: head[1].trim(), items: [] }; groups.push(cur); }
  }
  if (!valid || valid.has(DOC_OVERVIEW_PATH)) {
    if (!groups.length) groups.push({ label: "Start here", items: [] });
    const top = groups[0].items;
    if (!top.some((i) => i.path === DOC_OVERVIEW_PATH)) top.unshift({ title: "Overview", path: DOC_OVERVIEW_PATH });
  }
  const out = groups.filter((g) => g.items.length);
  return out.length ? out : null;
}

// Acronyms to upper-case, and special tokens with non-obvious casing, when
// turning a repo identifier into a readable display name.
const NAME_ACRONYMS = new Set([
  "api", "ocr", "id", "bff", "sdk", "tg", "ot", "poa", "pad", "kms", "ml",
  "e2ee", "vlm", "cdc", "mdl", "ui", "db", "cli", "aws", "sso", "jwt", "rest", "ekyc", "ekyb",
]);
const NAME_SPECIAL: Record<string, string> = {
  incodesmile: "IncodeSmile", facedb: "FaceDB", wasm: "WASM", protorepo: "Protorepo",
  go: "Go", java: "Java", "3po": "3PO",
};

// Repos with an established public-documentation product name (developer.incode.com).
// These override the derived name so System Docs reads in the same terms as the
// public docs — see COMPARISON-WITH-PUBLIC-DOCUMENTATION.md (terminology table).
// Keyed by the lower-cased repo identifier.
const PUBLIC_PRODUCT_NAME: Record<string, string> = {
  "ms-incode-dashboard": "Omni Dashboard",
  "dashboard-bff": "Omni Dashboard BFF",
  "ms-incodesmile-user-service": "Omni API",
  "ms-incodesmile-web": "Web SDK",
  "incdonboarding": "iOS SDK",
  "smiletoonboard_android": "Android SDK",
  "incdrecogkit": "iOS Computer Vision",
  "photorecandroid": "Android Computer Vision",
  "flutterwrapper": "Flutter SDK",
  "reactnativewrapper": "React Native SDK",
  "cordovawrapper": "Cordova SDK",
};

// Turn a repository identifier into a user-friendly, readable short name for the
// docs menu and page titles. First honors public-doc product names; otherwise
// drops the `ms-` microservice prefix, expands the Trust-Graph `global-tg-`
// prefix, upper-cases known acronyms, and leaves already-readable phrases and
// product CamelCase names (IncdRecogKit) intact.
export function friendlyName(name: string): string {
  const raw = name.trim();
  const publicName = PUBLIC_PRODUCT_NAME[raw.toLowerCase()];
  if (publicName) return publicName;
  if (!raw || /\s/.test(raw)) return raw;                  // already a phrase
  if (/[A-Z]/.test(raw)) return raw.replace(/[-_]+/g, " "); // product CamelCase name
  const s = raw.replace(/^ms[-_]/, "").replace(/^global-tg-/, "trust-graph-");
  return s
    .split(/[-_]+/)
    .filter(Boolean)
    .map((w) => NAME_SPECIAL[w] ?? (NAME_ACRONYMS.has(w) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(" ");
}

// Fallback: group every doc by top-level folder with friendly titles.
export function flatMenu(docs: DocEntry[]): MenuGroup[] {
  const by: Record<string, DocEntry[]> = {};
  for (const d of docs) (by[d.dir] ??= []).push(d);
  const order = Object.keys(by).sort((a, b) => (a === "" ? -1 : b === "" ? 1 : a.localeCompare(b)));
  return order.map((dir) => ({
    label: dir === "" ? "System" : dir.charAt(0).toUpperCase() + dir.slice(1),
    items: by[dir].map((d) => ({ title: friendlyName(d.title), path: d.path })),
  }));
}

// Build the org-scoped doc menu: filter to the org's docs, parse its OVERVIEW
// "Documentation map", and qualify paths back to docs-root-relative form.
export async function buildDocMenu(
  org: OrgId,
  allDocs: DocEntry[],
  fetchOverview: (prefix: string) => Promise<string>,
): Promise<{ menu: MenuGroup[]; docPaths: Set<string>; home: string } | null> {
  const prefix = org + "/";
  const orgDocs = allDocs.filter((d) => d.path.startsWith(prefix));
  if (!orgDocs.length) return null;
  const docPaths = new Set(orgDocs.map((d) => d.path));
  const orgRel = new Set(orgDocs.map((d) => d.path.slice(prefix.length)));

  let menu: MenuGroup[] | null = null;
  try {
    const overview = await fetchOverview(prefix);
    menu = parseDocMenu(overview, orgRel);
    if (menu) for (const g of menu) for (const it of g.items) it.path = prefix + it.path;
  } catch { /* fall through */ }
  if (!menu) menu = flatMenu(orgDocs);

  const home = prefix + DOC_OVERVIEW_PATH;
  const first = menu[0] && menu[0].items[0];
  const defaultPath = docPaths.has(home) ? home : first ? first.path : orgDocs[0].path;
  return { menu, docPaths, home: defaultPath };
}
